<template>
    <div
      v-if="show"
      class="modal fixed inset-0 bg-opacity-85 backdrop-blur-sm overflow-y-auto h-full w-full flex items-center justify-center z-[1000]"
      style="background-color: rgba(10, 25, 47, 0.85);"
      @click.self="closeModal"
      aria-modal="true"
      role="dialog"
      aria-labelledby="jockeyPerformanceModalTitle"
    >
      <div class="modal-content relative mx-auto w-full max-w-xl">
        <div class="flex justify-between items-center pb-4 mb-4 border-b border-gray-700">
          <h3 id="jockeyPerformanceModalTitle" class="text-xl font-bold modal-title">{{ jockeyName }}</h3>
          <div class="flex items-center gap-4">
            <div v-if="jockeyPrice !== null" class="jockey-price">
              <span class="price-label">Hinta:</span>
              <span class="price-value">{{ formatPrice(jockeyPrice) }} €</span>
            </div>
            <button
              class="close-modal"
              @click="closeModal"
              aria-label="Close modal"
            >&times;</button>
          </div>
        </div>

        <div class="modal-body max-h-[70vh] overflow-y-auto pr-1">
          <div v-if="isLoading" class="text-center p-4">
            <p class="text-gray-400">Ladataan suoritustietoja...</p>
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-accent mx-auto mt-2"></div>
          </div>

          <div v-else-if="error" class="error-message p-3 rounded text-sm">
            Virhe suoritustietojen lataamisessa: {{ error }}
          </div>

          <div v-else>
             <table v-if="performanceData.length > 0" class="performance-table">
              <thead class="sticky top-0">
                <tr>
                  <th scope="col" class="text-left">Päivämäärä</th>
                  <th scope="col" class="text-right">Lähdöt yht.</th>
                  <th scope="col" class="text-right">Voitot (2 p.)</th>
                  <th scope="col" class="text-right">Sijoitukset (1 p.)</th>
                  <th scope="col" class="text-right">Pisteet yht.</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="dayStats in performanceData" :key="dayStats.RaceDate">
                  <td class="text-left">{{ dayStats.RaceDate }}</td>
                  <td class="text-right">{{ dayStats.TotalRaces !== null ? dayStats.TotalRaces : '-' }}</td>
                  <td class="text-right">{{ dayStats.Wins !== null ? dayStats.Wins : '-' }}</td>
                  <td class="text-right">{{ dayStats.Places !== null ? dayStats.Places : '-' }}</td>
                  <td class="text-right total-points">{{ dayStats.TotalPoints !== null ? dayStats.TotalPoints : '-' }}</td>
                </tr>
              </tbody>
            </table>
            <p v-else class="no-data-message">
              Ei suoritustietoja tälle ohjastajalle valitulta historiaväliltä.
            </p>
          </div>
        </div>

        <div class="confirmation-buttons pt-4 border-t mt-4 text-center">
          <button @click="closeModal" class="px-4 py-2 bg-accent text-dark-blue rounded hover:bg-accent-hover transition-all duration-200 font-medium">Sulje</button>
        </div>
      </div>
    </div>
  </template>

  <script>
  import { ref, watch } from 'vue';
  import axios from '../plugins/axios'; // Adjust path as needed

  export default {
    name: 'JockeyPerformanceModal',
    props: {
      show: {
        type: Boolean,
        required: true,
      },
      jockeyId: {
        type: [String, Number],
        default: null,
      },
      jockeyName: {
        type: String,
        default: 'Valittu ohjastaja',
      },
    },
    emits: ['close'], // Declare the 'close' event
    setup(props, { emit }) {
      const HISTORY_DAYS = 365;
      // Reactive state for performance data, loading status, and errors
      const performanceData = ref([]);
      const isLoading = ref(false);
      const error = ref(null);
      const jockeyPrice = ref(null);

      // Function to fetch jockey performance data from the API
      const fetchJockeyPerformanceData = async () => {
        if (!props.jockeyId) {
          error.value = "Ohjastajan ID puuttuu.";
          performanceData.value = [];
          isLoading.value = false;
          return;
        }
        isLoading.value = true;
        error.value = null;
        performanceData.value = []; // Clear previous data

        const params = new URLSearchParams();
        params.set('history_days', String(HISTORY_DAYS));

        // Construct the API URL using the jockeyId prop
        const apiUrl = `${process.env.VUE_APP_API_BASE_URL}/jockeyinfo/${props.jockeyId}?${params.toString()}`;
        console.log(`Fetching jockey performance from: ${apiUrl}`);

        try {
          const response = await axios.get(apiUrl);
           if (Array.isArray(response.data)) {
            // Sort data by date descending before assigning
            performanceData.value = response.data.sort((a, b) => new Date(b.RaceDate) - new Date(a.RaceDate));
          } else {
              console.warn("Received non-array data for jockey performance:", response.data);
              performanceData.value = []; // Ensure it's an array
              error.value = "Odottamaton tietomuoto vastaanotettu.";
          }
        } catch (err) {
          console.error(`Error fetching performance for jockey ${props.jockeyId}:`, err.response?.data || err.message);
          error.value = err.response?.data?.error || err.message || 'Ohjastajan suoritustietoja ei voitu ladata.';
          performanceData.value = []; // Clear data on error
        } finally {
          isLoading.value = false; // Ensure loading state is always turned off
        }
      };

      // Function to fetch jockey price
      const fetchJockeyPrice = async () => {
        if (!props.jockeyId) {
          jockeyPrice.value = null;
          return;
        }

        try {
          const priceUrl = `${process.env.VUE_APP_API_BASE_URL}/jockey_price/${props.jockeyId}`;
          console.log(`Fetching jockey price from: ${priceUrl}`);
          const response = await axios.get(priceUrl);
          jockeyPrice.value = response.data.price;
        } catch (err) {
          console.error(`Error fetching price for jockey ${props.jockeyId}:`, err.response?.data || err.message);
          jockeyPrice.value = null; // Set to null if price fetch fails
        }
      };

      // Function to format price with thousands separator
      const formatPrice = (price) => {
        if (price === null || price === undefined) return '0';
        return new Intl.NumberFormat('fi-FI').format(price);
      };

      // Function to close the modal by emitting the 'close' event
      const closeModal = () => {
        emit('close');
      };

      // Watch for changes in the 'show' prop
      watch(() => props.show, (newValue) => {
        if (newValue && props.jockeyId) {
          // Fetch data when the modal becomes visible and has a jockeyId
          fetchJockeyPerformanceData();
          fetchJockeyPrice();
        } else if (!newValue) {
          // Reset state when the modal is hidden
          performanceData.value = [];
          error.value = null;
          isLoading.value = false;
          jockeyPrice.value = null;
        }
      });

       // Optional: Watch for jockeyId changes if the modal might stay open while ID changes
       watch(() => props.jockeyId, (newId, oldId) => {
          if (props.show && newId && newId !== oldId) {
              fetchJockeyPerformanceData();
              fetchJockeyPrice();
          }
       });

      // Return reactive refs and methods to be used in the template
      return {
        performanceData,
        isLoading,
        error,
        jockeyPrice,
        fetchJockeyPerformanceData,
        fetchJockeyPrice,
        formatPrice,
        closeModal,
      };
    },
  };
  </script>

  <style scoped>
  /* Styles specific to the JockeyPerformanceModal */

  /* Basic spinner animation */
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  .animate-spin {
    animation: spin 1s linear infinite;
  }

  /* Modal Base Styling */
  .modal-content {
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    background-color: #0a192f;
    padding: 2rem;
    border-radius: 12px;
    box-shadow: 0 10px 30px -10px rgba(2, 12, 27, 0.7);
    border: 1px solid #1e3a5f;
    color: #ccd6f6;
  }

  .modal-body {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    padding: 0.5rem;
  }

  /* Modal Title Styling */
  .modal-title {
    color: #64ffda;
    font-weight: 600;
    letter-spacing: 0.5px;
  }

  /* Close Button Styling */
  .close-modal {
    background: none;
    border: none;
    font-size: 1.75rem;
    line-height: 1;
    cursor: pointer;
    color: #8892b0;
    transition: color 0.3s ease;
  }
  .close-modal:hover {
    color: #64ffda;
  }

  /* Jockey Price Styling */
  .jockey-price {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    text-align: right;
  }

  .price-label {
    font-size: 0.75rem;
    color: #8892b0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
  }

  .price-value {
    font-size: 1rem;
    font-weight: 600;
    color: #64ffda;
    letter-spacing: 0.5px;
  }

  /* Error message styling */
  .error-message {
    background-color: rgba(255, 107, 107, 0.1);
    border: 1px solid rgba(255, 107, 107, 0.3);
    color: #ff6b6b;
    padding: 1rem;
  }

  /* No data message styling */
  .no-data-message {
    font-style: italic;
    color: #8892b0;
    text-align: center;
    padding: 2rem 1rem;
  }

  /* Performance Table Styling */
  .performance-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    color: #ccd6f6;
  }

  .performance-table thead {
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .performance-table th {
    background-color: #1e3a5f;
    color: #e6f1ff;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    padding: 0.75rem 1rem;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #112240;
  }

  .performance-table tbody tr {
    transition: background-color 0.2s ease;
    border-bottom: 1px solid rgba(100, 255, 218, 0.1);
  }

  .performance-table tbody tr:hover {
    background-color: rgba(100, 255, 218, 0.05);
  }

  .performance-table td {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
    color: #a8b2d1;
  }

  .performance-table .total-points {
    font-weight: 700;
    color: #64ffda;
  }

  /* Button accent colors */
  .bg-accent {
    background-color: #64ffda;
  }
  .bg-accent-hover {
    background-color: #52d6c0;
  }
  .text-dark-blue {
    color: #0a192f;
  }
  .border-accent {
    border-color: #64ffda;
  }

  /* Improved button styling */
  button {
    padding: 0.75rem 1.5rem;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.3s ease;
    border: none;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border-radius: 6px;
  }

  button.bg-accent {
    background-color: #64ffda;
    color: #0a192f;
  }

  button.bg-accent:hover {
    background-color: #52d6c0;
    transform: translateY(-2px);
    box-shadow: 0 6px 10px rgba(0, 0, 0, 0.15);
  }

  /* Confirmation buttons styling */
  .confirmation-buttons {
    margin-top: 1rem;
    display: flex;
    justify-content: center;
    gap: 1rem;
  }

  /* Scrollbar styling */
  .modal-body::-webkit-scrollbar {
    width: 6px;
  }
  .modal-body::-webkit-scrollbar-track {
    background: #0a192f;
    border-radius: 3px;
  }
  .modal-body::-webkit-scrollbar-thumb {
    background: #1e3a5f;
    border-radius: 3px;
  }
  .modal-body::-webkit-scrollbar-thumb:hover {
    background: #2d4a6d;
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .modal-content {
      padding: 1.5rem;
    }

    .performance-table th,
    .performance-table td {
      padding: 0.5rem 0.75rem;
      font-size: 0.8rem;
    }
  }

  @media (max-width: 480px) {
    .modal-content {
      padding: 1rem;
    }

    .performance-table {
      font-size: 0.75rem;
    }

    .performance-table th,
    .performance-table td {
      padding: 0.4rem 0.5rem;
    }
  }
  </style>

